import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AuthService from "../../../api/AuthService";
import { communicationService } from "../../../api";
import { useAlert } from "../../../context/AlertContext";
import { getPriorityMeta, relativeTime, PRIORITY_META } from "../../../utils/notificationMeta";

const AUDIENCES = [
  { value: "all", label: "All users" },
  { value: "company", label: "Company" },
  { value: "role", label: "Role" },
  { value: "user", label: "Specific user" },
];
const ROLE_OPTIONS = ["SuperAdmin", "SubAdmin", "Admin", "Employee"];
const PRIORITIES = Object.keys(PRIORITY_META); // low | normal | high | critical

const emptyForm = {
  title: "",
  body: "",
  priority: "normal",
  expiresAt: "",
  audience: "all",
};

// List envelope: { data: { data: [...] } }.
const extractList = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  return d?.data ?? [];
};

const NotificationBroadcast = () => {
  const { showAlert } = useAlert();
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Target selection (depends on audience).
  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [userQuery, setUserQuery] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  // List.
  const [broadcasts, setBroadcasts] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const loadBroadcasts = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await AuthService.getAdminNotifications();
      if (res?.success !== false) setBroadcasts(extractList(res));
    } catch (e) {
      /* ignore */
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadBroadcasts();
    // Company options for the "company" audience.
    (async () => {
      try {
        const res = await AuthService.getCompantList();
        if (res?.success) setCompanyOptions(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        /* ignore */
      }
    })();
  }, [loadBroadcasts]);

  // Debounced user search for the "user" audience.
  useEffect(() => {
    if (form.audience !== "user" || !userQuery.trim()) return undefined;
    const t = setTimeout(async () => {
      try {
        const res = await communicationService.searchUsers(userQuery.trim());
        const list = Array.isArray(res?.data) ? res.data : extractList(res);
        setUserOptions(list || []);
      } catch (e) {
        /* ignore */
      }
    }, 350);
    return () => clearTimeout(t);
  }, [userQuery, form.audience]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setSelectedCompany(null);
    setSelectedRole("");
    setSelectedUser(null);
    setUserQuery("");
  };

  const resolveTarget = () => {
    switch (form.audience) {
      case "company":
        return selectedCompany?._id || selectedCompany?.id || "";
      case "role":
        return selectedRole;
      case "user":
        return selectedUser?._id || selectedUser?.id || "";
      default:
        return "";
    }
  };

  const validate = () => {
    if (!form.title.trim()) return "Title is required.";
    if (!form.body.trim()) return "Body is required.";
    if (form.audience !== "all" && !resolveTarget()) {
      return "Please choose a target for the selected audience.";
    }
    return "";
  };

  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      showAlert(error, "error");
      return;
    }
    // Nested targetAudience per the Joi schema. `type` is not sent — the
    // server hardcodes notification type to "manual".
    const targetAudience = { type: form.audience };
    if (form.audience === "company") targetAudience.companyId = resolveTarget();
    if (form.audience === "role") targetAudience.role = resolveTarget();
    if (form.audience === "user") targetAudience.recipientId = resolveTarget();

    const payload = {
      title: form.title.trim(),
      body: form.body.trim(),
      priority: form.priority,
      isPublished: true,
      targetAudience,
      ...(form.expiresAt ? { expiresAt: new Date(form.expiresAt).toISOString() } : {}),
    };

    setSubmitting(true);
    try {
      const res = editingId
        ? await AuthService.updateAdminNotification(editingId, payload)
        : await AuthService.createAdminNotification(payload);
      if (res?.success !== false) {
        showAlert(
          editingId ? "Broadcast updated." : "Broadcast sent.",
          "success"
        );
        resetForm();
        loadBroadcasts();
      } else {
        showAlert(res?.message || "Could not save the broadcast.", "error");
      }
    } catch (e) {
      showAlert(e?.message || "Could not save the broadcast.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (b) => {
    const audience = b?.audienceType || b?.targetAudience?.type || "all";
    setEditingId(b?._id || b?.id); // _id is the broadcastId
    setForm({
      title: b?.title || "",
      body: b?.body || "",
      priority: b?.priority || "normal",
      expiresAt: b?.expiresAt ? String(b.expiresAt).slice(0, 16) : "",
      audience,
    });
    // Best-effort restore of target (role resolves; company/user need reselect).
    setSelectedRole(audience === "role" ? b?.targetAudience?.role || "" : "");
    setSelectedCompany(null);
    setSelectedUser(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget?._id || deleteTarget?.id;
    try {
      const res = await AuthService.deleteAdminNotification(id);
      if (res?.success !== false) {
        showAlert("Broadcast deleted.", "success");
        if (editingId === id) resetForm();
        loadBroadcasts();
      } else {
        showAlert(res?.message || "Could not delete the broadcast.", "error");
      }
    } catch (e) {
      showAlert(e?.message || "Could not delete the broadcast.", "error");
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ fontSize: "22px", fontWeight: 700, mb: 2 }}>
        Notification Broadcasts
      </Typography>

      {/* Composer */}
      <Card sx={{ p: 3, mb: 3, borderRadius: "14px" }}>
        <Typography sx={{ fontWeight: 600, mb: 2 }}>
          {editingId ? "Edit broadcast" : "New broadcast"}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography sx={{ fontSize: "13px", mb: 0.5 }}>Send to</Typography>
            <Select
              fullWidth
              size="small"
              value={form.audience}
              onChange={(e) => setField("audience", e.target.value)}
            >
              {AUDIENCES.map((a) => (
                <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
              ))}
            </Select>
          </Grid>

          {/* Conditional target */}
          <Grid item xs={12} md={4}>
            {form.audience === "company" && (
              <>
                <Typography sx={{ fontSize: "13px", mb: 0.5 }}>Company</Typography>
                <Autocomplete
                  size="small"
                  options={companyOptions}
                  value={selectedCompany}
                  onChange={(e, v) => setSelectedCompany(v)}
                  getOptionLabel={(o) => o?.companyName || o?.name || ""}
                  isOptionEqualToValue={(o, v) => (o?._id || o?.id) === (v?._id || v?.id)}
                  renderInput={(params) => <TextField {...params} placeholder="Select company" />}
                />
              </>
            )}
            {form.audience === "role" && (
              <>
                <Typography sx={{ fontSize: "13px", mb: 0.5 }}>Role</Typography>
                <Select
                  fullWidth
                  size="small"
                  displayEmpty
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  <MenuItem value="" disabled>Select role</MenuItem>
                  {ROLE_OPTIONS.map((r) => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </Select>
              </>
            )}
            {form.audience === "user" && (
              <>
                <Typography sx={{ fontSize: "13px", mb: 0.5 }}>User</Typography>
                <Autocomplete
                  size="small"
                  options={userOptions}
                  value={selectedUser}
                  onChange={(e, v) => setSelectedUser(v)}
                  onInputChange={(e, v) => setUserQuery(v)}
                  filterOptions={(x) => x}
                  getOptionLabel={(o) =>
                    o?.email || o?.name ||
                    [o?.personalInfo?.firstName, o?.personalInfo?.surName].filter(Boolean).join(" ") ||
                    ""
                  }
                  isOptionEqualToValue={(o, v) => (o?._id || o?.id) === (v?._id || v?.id)}
                  renderInput={(params) => <TextField {...params} placeholder="Search users" />}
                />
              </>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography sx={{ fontSize: "13px", mb: 0.5 }}>Priority</Typography>
            <Select
              fullWidth
              size="small"
              value={form.priority}
              onChange={(e) => setField("priority", e.target.value)}
            >
              {PRIORITIES.map((p) => (
                <MenuItem key={p} value={p} sx={{ textTransform: "capitalize" }}>{p}</MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item xs={12}>
            <Typography sx={{ fontSize: "13px", mb: 0.5 }}>Title</Typography>
            <TextField
              fullWidth
              size="small"
              value={form.title}
              onChange={(e) => setField("title", e.target.value)}
              placeholder="Notification title"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography sx={{ fontSize: "13px", mb: 0.5 }}>Body</Typography>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={3}
              value={form.body}
              onChange={(e) => setField("body", e.target.value)}
              placeholder="Message body"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography sx={{ fontSize: "13px", mb: 0.5 }}>Expires at (optional)</Typography>
            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              value={form.expiresAt}
              onChange={(e) => setField("expiresAt", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", gap: 1.5, mt: 3 }}>
          <LoadingButton
            loading={submitting}
            variant="contained"
            onClick={handleSubmit}
            sx={{ textTransform: "none", backgroundColor: "#000" }}
          >
            {editingId ? "Update broadcast" : "Send broadcast"}
          </LoadingButton>
          {editingId && (
            <Button onClick={resetForm} sx={{ textTransform: "none" }}>
              Cancel
            </Button>
          )}
        </Box>
      </Card>

      {/* List */}
      <Card sx={{ p: 0, borderRadius: "14px", overflow: "hidden" }}>
        <Typography sx={{ fontWeight: 600, p: 2 }}>Sent broadcasts</Typography>
        {loadingList ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : broadcasts.length === 0 ? (
          <Typography sx={{ color: "#878787", fontSize: "13px", p: 3 }}>
            No broadcasts yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Audience</TableCell>
                  <TableCell align="right">Recipients</TableCell>
                  <TableCell align="right">Read</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {broadcasts.map((b) => {
                  const id = b?._id || b?.id;
                  const pMeta = getPriorityMeta(b?.priority);
                  const recipients = b?.recipientCount ?? "—";
                  const read = b?.readCount ?? "—";
                  return (
                    <TableRow key={id} hover>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>{b?.title}</Typography>
                        <Typography sx={{ fontSize: "12px", color: "#888" }} noWrap>
                          {b?.body || b?.message}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {pMeta ? (
                          <Chip
                            label={pMeta.label}
                            size="small"
                            sx={{ backgroundColor: pMeta.color, color: "#fff", height: 20, fontSize: "11px" }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: "12px" }}>{b?.priority || "—"}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: "12px", textTransform: "capitalize" }}>
                        {b?.audienceType || "—"}
                      </TableCell>
                      <TableCell align="right">{recipients}</TableCell>
                      <TableCell align="right">{read}</TableCell>
                      <TableCell sx={{ fontSize: "12px", color: "#888" }}>
                        {relativeTime(b?.createdAt || b?.date)}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleEdit(b)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => setDeleteTarget(b)}>
                          <DeleteOutlineIcon fontSize="small" color="error" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete broadcast?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "14px" }}>
            &quot;{deleteTarget?.title}&quot; will be permanently deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" sx={{ textTransform: "none" }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationBroadcast;
